// Regole di inserimento/modifica worklog, estratte dalla Daily Tracking classica (stesso comportamento).
import { WORKING_DAYS_CONFIG } from '../config/local.config';
import { parseLocalDate } from './dateHelpers';
import { parseEffort } from './effortParser';

const normalize = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const formatDDMMYYYY = (date) =>
  `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;

function getWorkingDaysPassedInMonth(currentDate) {
  let count = 0;
  for (let day = 1; day <= currentDate.getDate(); day++) {
    const d = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (WORKING_DAYS_CONFIG.workingDaysOfWeek.includes(d.getDay())) count++;
  }
  return count;
}

export function addWorkingDays(startDate, days) {
  const result = normalize(startDate);
  const direction = days > 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (WORKING_DAYS_CONFIG.workingDaysOfWeek.includes(result.getDay())) remaining--;
  }
  return result;
}

export function isWithinPreviousMonthEditingPeriod() {
  return getWorkingDaysPassedInMonth(new Date()) <= WORKING_DAYS_CONFIG.previousMonthEditingDays;
}

/** Blocco generale per data (inserimento, modifica, cancellazione). */
export function isDateLocked(date) {
  if (!date) return true;
  const today = new Date();
  const sel = normalize(date);
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  if (sel < firstOfPreviousMonth) return true;
  if (sel < firstOfCurrentMonth) return !isWithinPreviousMonthEditingPeriod();
  return false;
}

export function getDateLockMessage(date) {
  const today = new Date();
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  if (normalize(date) < firstOfPreviousMonth) return 'Worklogs can’t be changed for months before the previous month.';
  return `The previous month can only be changed within the first ${WORKING_DAYS_CONFIG.previousMonthEditingDays} working day(s) of the current month.`;
}

function isOutsideProjectWindow(sel, project) {
  const today = normalize(new Date());
  if (project.days_before_block != null && sel < addWorkingDays(today, -project.days_before_block)) return true;
  if (project.days_after_block != null && sel > addWorkingDays(today, project.days_after_block)) return true;
  return false;
}

/** Vincoli del progetto: fine progetto, mese precedente bloccato, finestra di giorni lavorativi. */
export function isDateBlockedForProject(date, project) {
  if (!date || !project) return false;
  const sel = normalize(date);
  const today = new Date();
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  if (project.end_date && sel > normalize(parseLocalDate(project.end_date))) return true;
  if (sel < firstOfPreviousMonth) return true;
  if (sel < firstOfCurrentMonth) {
    if (!isWithinPreviousMonthEditingPeriod()) return true;
    if (project.previous_month_blocked === true) return true;
  }
  return isOutsideProjectWindow(sel, project);
}

export function getProjectBlockMessage(date, project) {
  if (!project) return '';
  const sel = normalize(date);
  const today = new Date();
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (project.end_date && sel > normalize(parseLocalDate(project.end_date))) {
    return `Project ended on ${formatDDMMYYYY(parseLocalDate(project.end_date))}.`;
  }
  if (sel < firstOfCurrentMonth && project.previous_month_blocked === true) return 'The previous month is blocked for this project.';
  if (isDateLocked(date)) return getDateLockMessage(date);
  const todayN = normalize(today);
  const from = project.days_before_block != null ? formatDDMMYYYY(addWorkingDays(todayN, -project.days_before_block)) : null;
  const to = project.days_after_block != null ? formatDDMMYYYY(addWorkingDays(todayN, project.days_after_block)) : null;
  if (from && to) return `Allowed dates for this project: ${from} to ${to}.`;
  if (from) return `Allowed dates for this project: from ${from}.`;
  if (to) return `Allowed dates for this project: up to ${to}.`;
  return '';
}

/** Progetti dell'utente validi alla data (attivo, inizio/fine progetto, validità assegnazione). */
export function filterProjectsForDate(projects, date) {
  const sel = normalize(date);
  return projects.filter((p) => {
    if (!p.active) return false;
    if (p.end_date && sel > normalize(parseLocalDate(p.end_date))) return false;
    if (p.start_date && sel < normalize(parseLocalDate(p.start_date))) return false;
    if (p.validity_end_date && sel > normalize(new Date(p.validity_end_date))) return false;
    return true;
  });
}

/**
 * Prefisso ticket bloccato, stessa regola della pagina classica:
 * attività che richiede ticket e il cui nome contiene "ticket resolution", "am" o "enh".
 */
export function getTicketPrefix(project, activity) {
  if (!project?.ticket_id_prefix || !activity?.requires_ticket_id) return '';
  const name = String(activity.name || '').toLowerCase();
  const isTicketResolution = name.includes('ticket resolution') || name.includes('am') || name.includes('enh');
  return isTicketResolution ? project.ticket_id_prefix : '';
}

export function cleanTicketId(ticketId, prefix) {
  if (!ticketId) return '';
  const trimmed = String(ticketId).trim();
  const p = String(prefix || '').trim();
  if (p && trimmed.startsWith(p)) return p + trimmed.slice(p.length).trim();
  return trimmed;
}

/** Ticket conforme alla regex del progetto (se definita); una regex non valida non blocca. */
export function isTicketFormatValid(project, ticketId) {
  if (!project?.regex) return true;
  try {
    let regexStr = String(project.regex).trim();
    let flags = '';
    if (regexStr.startsWith('/') && regexStr.lastIndexOf('/') > 0) {
      const lastSlash = regexStr.lastIndexOf('/');
      flags = regexStr.slice(lastSlash + 1);
      regexStr = regexStr.slice(1, lastSlash);
    }
    const prefix = String(project.ticket_id_prefix || '').trim();
    const raw = String(ticketId || '').trim();
    const toValidate = prefix && raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
    return new RegExp(regexStr, flags).test(toValidate);
  } catch (e) {
    console.warn('Invalid project regex:', project.regex, e);
    return true;
  }
}

export const MAX_DESCRIPTION_LENGTH = 400;
export const DAILY_WARNING_MINUTES = 480;

/** Errori di un worklog (campi del form); oggetto vuoto se valido. */
export function validateWorklog({ project, activity, effortTypeId, ticket, effort, description }, prefix, date) {
  const e = {};
  if (!project) e.project = 'Required';
  else if (isDateBlockedForProject(date, project)) e.project = getProjectBlockMessage(date, project) || 'Not allowed on this date';
  if (!activity) e.activity = 'Required';
  if (!effortTypeId) e.effortTypeId = 'Required';
  const parsed = parseEffort(effort);
  if (parsed.empty) e.effort = 'Required';
  else if (!parsed.valid) e.effort = parsed.error;
  const t = ticket.trim();
  if (activity?.requires_ticket_id) {
    if (!t || t === prefix) e.ticket = 'Required for this activity';
    else if (!isTicketFormatValid(project, t)) e.ticket = project?.regex_desc || 'Invalid format for this project';
  }
  if (activity?.requires_description && !description.trim()) e.description = 'Required for this activity';
  if (description.length > MAX_DESCRIPTION_LENGTH) e.description = `Max ${MAX_DESCRIPTION_LENGTH} characters`;
  return e;
}

/**
 * Motivo per cui un worklog esistente non si può modificare o eliminare ('' se si può),
 * stessa regola della lista classica: approvato, mese bloccato, mese precedente bloccato dal progetto.
 */
export function getWorklogLockReason(worklog, project) {
  if (worklog.status_name === 'Approved') return 'Approved worklogs can’t be changed.';
  const date = parseLocalDate(String(worklog.date).slice(0, 10));
  if (isDateLocked(date)) return getDateLockMessage(date);
  const today = new Date();
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  if (project?.previous_month_blocked === true && normalize(date) < firstOfCurrentMonth) return 'The previous month is blocked for this project.';
  return '';
}
